import React, { useState } from 'react';
import { OverlayPanel } from '../ui/OverlayPanel';
import { FACTION_INFO } from '../constants/factions';

export interface DialogueOption {
  text: string;
  npcResponse: string;
  questEvent?: string;
}

interface NPCDialogueModalProps {
  isOpen: boolean;
  npcId: string;
  npcName: string;
  npcTitle: string;
  npcPortrait: string;
  dialogue: string;
  npcFaction?: string | null;
  questDialogue?: {
    start?: string;
    progress?: string;
    complete?: string;
  };
  questProgress?: number; // 0-100
  dialogueOptions?: DialogueOption[]; // Player reply options like Runescape
  onClose: () => void;
  onSelectOption?: (option: DialogueOption) => void;
}

const FONT = {
  family: '"Sometype Mono", monospace',
  sizeSmall: 11,
  sizeMedium: 12,
  sizeLarge: 13,
  weightNormal: 400,
  weightBold: 700,
};

const COLORS = {
  text: '#e6edf3',
  textSecondary: '#8b949e',
  bgPrimary: '#0d1117',
  bgSecondary: '#0f172a',
  border: '#30363d',
  accentTeal: '#58a6ff',
};

export const NPCDialogueModal: React.FC<NPCDialogueModalProps> = ({
  isOpen,
  npcId,
  npcName,
  npcTitle,
  npcPortrait,
  dialogue,
  npcFaction,
  questDialogue,
  questProgress = 0,
  dialogueOptions,
  onClose,
  onSelectOption,
}) => {
  const [currentStage, setCurrentStage] = useState<'npc' | 'player' | 'npc_response'>('npc');
  const [selectedOption, setSelectedOption] = useState<DialogueOption | null>(null);

  // Determine which dialogue to display based on quest progress
  const getDisplayDialogue = (): string => {
    if (!questDialogue) return dialogue;
    
    if (questProgress >= 100) {
      return questDialogue.complete || dialogue;
    } else if (questProgress > 0) {
      return questDialogue.progress || dialogue;
    } else {
      return questDialogue.start || dialogue;
    }
  };
  
  const displayDialogue = getDisplayDialogue();

  const handleSelectOption = (option: DialogueOption) => {
    setSelectedOption(option);
    setCurrentStage('npc_response');
    if (onSelectOption) {
      onSelectOption(option);
    }
  };

  const handleContinue = () => {
    // If NPC dialogue has player choice options, show them
    if (currentStage === 'npc' && dialogueOptions && dialogueOptions.length > 0) {
      setCurrentStage('player');
    } else {
      // Otherwise, automatically close (no choices needed)
      onClose();
      setCurrentStage('npc');
      setSelectedOption(null);
    }
  };

  return (
    <OverlayPanel
      isOpen={isOpen}
      onClose={() => {
        onClose();
        setCurrentStage('npc');
        setSelectedOption(null);
      }}
      title={npcName.toUpperCase()}
      draggable
      storageKey="npcDialoguePosition"
      defaultPosition={{ x: 200, y: 150 }}
      clampToViewport
      closeOnEscape
      closeOnBackdrop
      width={600}
    >
      <div
        style={{
          padding: '16px',
          fontFamily: FONT.family,
          display: 'flex',
          gap: '16px',
        }}
      >
        {/* NPC Portrait */}
        <div
          style={{
            flex: '0 0 120px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <img
            src={npcPortrait}
            alt={npcName}
            style={{
              width: '120px',
              height: '120px',
              borderRadius: '4px',
              border: `1px solid ${COLORS.border}`,
              objectFit: 'cover',
            }}
            onError={(e) => {
              (e.target as HTMLImageElement).src =
                'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="120" height="120"%3E%3Crect fill="%230f172a" width="120" height="120"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%238b949e" font-size="12"%3E[NPC]%3C/text%3E%3C/svg%3E';
            }}
          />
          <div
            style={{
              fontSize: FONT.sizeSmall,
              color: COLORS.textSecondary,
              textAlign: 'center',
              maxWidth: '120px',
            }}
          >
            {npcTitle}
          </div>
          {npcFaction && FACTION_INFO[npcFaction] && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 8px',
                backgroundColor: '#21262d',
                border: `1px solid ${FACTION_INFO[npcFaction].color}`,
                borderRadius: '4px',
                maxWidth: '120px',
              }}
            >
              <img
                src={`${process.env.PUBLIC_URL || ''}${FACTION_INFO[npcFaction].icon}`}
                alt={FACTION_INFO[npcFaction].name}
                style={{ width: 20, height: 20, objectFit: 'contain' }}
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
              <div
                style={{
                  fontSize: 9,
                  color: FACTION_INFO[npcFaction].color,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                }}
              >
                {FACTION_INFO[npcFaction].name}
              </div>
            </div>
          )}
        </div>

        {/* Dialogue Content */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
          }}
        >
          {/* NPC Name */}
          <div
            style={{
              fontSize: FONT.sizeLarge,
              fontWeight: FONT.weightBold,
              color: COLORS.accentTeal,
              borderBottom: `1px solid ${COLORS.border}`,
              paddingBottom: '8px',
            }}
          >
            {npcName}
          </div>

          {/* NPC Dialogue Stage */}
          {currentStage === 'npc' && (
            <>
              <div
                style={{
                  fontSize: FONT.sizeMedium,
                  color: COLORS.text,
                  lineHeight: 1.6,
                  maxHeight: '300px',
                  overflowY: 'auto',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {displayDialogue}
              </div>
              <button
                onClick={handleContinue}
                style={{
                  marginTop: '12px',
                  padding: '8px 16px',
                  backgroundColor: COLORS.accentTeal,
                  color: COLORS.bgPrimary,
                  border: 'none',
                  borderRadius: '2px',
                  fontFamily: FONT.family,
                  fontSize: FONT.sizeMedium,
                  fontWeight: FONT.weightBold,
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                }}
                onMouseEnter={(e) => {
                  (e.target as HTMLButtonElement).style.backgroundColor = '#79c0ff';
                }}
                onMouseLeave={(e) => {
                  (e.target as HTMLButtonElement).style.backgroundColor = COLORS.accentTeal;
                }}
              >
                {dialogueOptions && dialogueOptions.length > 0 ? 'CONTINUE' : 'CLOSE'}
              </button>
            </>
          )}

          {/* Player Reply Options Stage */}
          {currentStage === 'player' && dialogueOptions && dialogueOptions.length > 0 && (
            <>
              <div style={{ color: COLORS.textSecondary, fontSize: FONT.sizeSmall, marginBottom: '8px' }}>
                What do you say?
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {dialogueOptions.map((option, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleSelectOption(option)}
                    style={{
                      padding: '8px 10px',
                      backgroundColor: COLORS.bgSecondary,
                      color: COLORS.accentTeal,
                      border: `1px solid ${COLORS.border}`,
                      borderRadius: '2px',
                      fontFamily: FONT.family,
                      fontSize: FONT.sizeSmall,
                      fontWeight: FONT.weightBold,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      textAlign: 'left',
                    }}
                    onMouseEnter={(e) => {
                      (e.target as HTMLButtonElement).style.backgroundColor = COLORS.border;
                      (e.target as HTMLButtonElement).style.color = '#fff';
                    }}
                    onMouseLeave={(e) => {
                      (e.target as HTMLButtonElement).style.backgroundColor = COLORS.bgSecondary;
                      (e.target as HTMLButtonElement).style.color = COLORS.accentTeal;
                    }}
                  >
                    → {option.text}
                  </button>
                ))}
              </div>
            </>
          )}

          {/* NPC Response Stage */}
          {currentStage === 'npc_response' && selectedOption && (
            <>
              <div style={{ color: COLORS.textSecondary, fontSize: FONT.sizeSmall, marginBottom: '8px', fontStyle: 'italic' }}>
                You: {selectedOption.text}
              </div>
              <div
                style={{
                  fontSize: FONT.sizeMedium,
                  color: COLORS.text,
                  lineHeight: 1.6,
                  maxHeight: '300px',
                  overflowY: 'auto',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  padding: '8px',
                  backgroundColor: COLORS.bgSecondary,
                  borderLeft: `2px solid ${COLORS.accentTeal}`,
                }}
              >
                {selectedOption.npcResponse}
              </div>
              <button
                onClick={() => onClose()}
                style={{
                  marginTop: '12px',
                  padding: '8px 16px',
                  backgroundColor: COLORS.accentTeal,
                  color: COLORS.bgPrimary,
                  border: 'none',
                  borderRadius: '2px',
                  fontFamily: FONT.family,
                  fontSize: FONT.sizeMedium,
                  fontWeight: FONT.weightBold,
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                }}
                onMouseEnter={(e) => {
                  (e.target as HTMLButtonElement).style.backgroundColor = '#79c0ff';
                }}
                onMouseLeave={(e) => {
                  (e.target as HTMLButtonElement).style.backgroundColor = COLORS.accentTeal;
                }}
              >
                CLOSE
              </button>
            </>
          )}
        </div>
      </div>
    </OverlayPanel>
  );
};
